// Вузол КТП на шині: статус, лічильник інверторів, потужність і секційні
// параметри. Клік виділяє КТП і оновлює нижню панель.
import { cx } from '@/i18n/cfgText';
import i18n from '@/i18n';
import { scada, stateColor, STATE_LABEL } from '@/scada/tokens';
import type { SchemeKtp } from './types';

/** Комплектна трансформаторна підстанція: корпус, дах, ізолятори. */
function KtpIcon({ color }: { color: string }) {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <rect x="4" y="8" width="18" height="13" rx="1.5"
        stroke={scada.textDim} strokeWidth="1.2" fill={scada.panel2} />
      <line x1="2.5" y1="8" x2="23.5" y2="8" stroke={scada.textDim} strokeWidth="1.4" strokeLinecap="round" />
      {/* Ізолятори вводу */}
      <line x1="9" y1="4.5" x2="9" y2="8" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="13" y1="3.5" x2="13" y2="8" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="17" y1="4.5" x2="17" y2="8" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
      {/* Двері */}
      <rect x="10" y="13" width="6" height="8" rx="0.8" stroke={scada.border} strokeWidth="1" />
    </svg>
  );
}

export default function KtpNode({ ktp, selected, onSelect }: {
  ktp: SchemeKtp;
  selected: boolean;
  onSelect: () => void;
}) {
  const c = stateColor(ktp.state);
  const degraded = ktp.working < ktp.total;

  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        textAlign: 'left', width: '100%', cursor: 'pointer',
        background: scada.panel,
        // Виділення — рамка кольору стану плюс тонкий обвід, без заливки:
        // заливка перебивала б кольори всередині картки.
        border: `1px solid ${selected ? c : ktp.state === 'ok' ? scada.border : `${c}55`}`,
        outline: selected ? `1px solid ${c}` : 'none',
        outlineOffset: 1,
        borderRadius: 10, padding: '10px 11px', display: 'grid', gap: 7,
        boxShadow: 'var(--shadow)', minWidth: 0,
        transition: 'border-color .15s ease',
      }}
    >
      <div className="flex items-center" style={{ gap: 7, minWidth: 0 }}>
        <KtpIcon color={c} />
        <div style={{ minWidth: 0 }}>
          <div className="font-display" style={{
            fontSize: 13, fontWeight: 700, color: scada.text, lineHeight: 1.1,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{cx(ktp.name)}</div>
          <div className="flex items-center" style={{ gap: 4, marginTop: 2 }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: c, flexShrink: 0 }} />
            <span className="font-display" style={{
              fontSize: 9, fontWeight: 600, letterSpacing: '.04em',
              textTransform: 'uppercase', color: c, whiteSpace: 'nowrap',
            }}>{STATE_LABEL[ktp.state]}</span>
          </div>
        </div>
      </div>

      <div className="tabular-nums" style={{
        fontSize: 10.5, color: degraded ? c : scada.textMuted, whiteSpace: 'nowrap',
      }}>
        {ktp.working}/{ktp.total} {i18n.t('ak_scada2_inverters_2')}
      </div>

      <div className="font-display tabular-nums" style={{
        fontSize: 19, fontWeight: 700, color: scada.text, lineHeight: 1,
      }}>
        {Math.round(ktp.powerKw)}<span style={{ fontSize: 10, fontWeight: 500, color: scada.textMuted }}> {i18n.t('ak_scada2_kw')}</span>
      </div>

      <div style={{ display: 'grid', gap: 3, borderTop: `1px solid ${scada.border}`, paddingTop: 6 }}>
        {[
          { k: i18n.t('ak_scada2_voltage'), v: `${ktp.voltageKv} ${i18n.t('ak_scada2_kv')}` },
          { k: i18n.t('ak_scada2_current'), v: `${ktp.currentA} A` },
          { k: i18n.t('ak_scada2_temperature'), v: `${ktp.tempC} °C` },
        ].map((r) => (
          <div key={r.k} className="flex items-baseline justify-between" style={{ gap: 8 }}>
            <span style={{ fontSize: 10, color: scada.textMuted, whiteSpace: 'nowrap' }}>{r.k}</span>
            <span className="tabular-nums" style={{ fontSize: 11, color: scada.textDim, whiteSpace: 'nowrap' }}>{r.v}</span>
          </div>
        ))}
      </div>
    </button>
  );
}
